import { Link } from 'react-router-dom';
import type { BottleDTO } from '@adega/shared';
import { URGENCY_LABELS, WINE_TYPE_LABELS, nomeCompleto } from '@adega/shared';
import { useCellars } from '../api/queries.js';

const COR_URGENCIA: Record<string, string> = {
  beber_agora: 'bg-red-500/20 text-red-200',
  beber_este_ano: 'bg-amber-500/20 text-amber-100',
  no_auge: 'bg-adega-gold/20 text-adega-gold',
  no_ponto: 'bg-emerald-500/15 text-emerald-200',
  guardar: 'bg-sky-500/15 text-sky-200',
  sem_informacao: 'bg-adega-surface-2 text-adega-muted',
};

export function BottleCard({
  bottle,
  extra,
  iguais,
}: {
  bottle: BottleDTO;
  extra?: React.ReactNode;
  /**
   * Garrafas iguais representadas por este cartão (incluindo `bottle`). Com mais
   * de uma, o cartão mostra a quantidade e todas as posições — em vez de repetir
   * o mesmo vinho N vezes na lista.
   */
  iguais?: BottleDTO[];
}) {
  // Hook em vez de prop: `BottleCard` é usado em várias telas, e a lista de
  // adegas já está em cache -- não custa requisição nova.
  const { data: adegas } = useCellars();
  const semAdegas = (adegas?.length ?? 0) === 0;

  const quantidade = iguais?.length ?? 1;
  const posicoes = (iguais ?? [bottle]).map((b) => b.positionLabel ?? 'sem posição');
  const subtitulo = [
    bottle.producer,
    bottle.vintage?.toString(),
    bottle.type ? WINE_TYPE_LABELS[bottle.type] : null,
    [bottle.region, bottle.country].filter(Boolean).join(', ') || null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Link to={`/garrafa/${bottle.id}`} className="card flex gap-3 transition hover:border-adega-wine-light">
      <div className="h-20 w-16 shrink-0 overflow-hidden rounded-lg bg-adega-surface-2">
        {bottle.thumbUrl ? (
          <img src={bottle.thumbUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <span aria-hidden className="grid h-full place-items-center text-2xl opacity-40">
            🍾
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold">
          {nomeCompleto(bottle)}
          {quantidade > 1 ? (
            <span className="ml-1.5 rounded-full bg-adega-wine px-1.5 py-0.5 align-middle text-[10px] font-bold text-white">
              ×{quantidade}
            </span>
          ) : null}
        </p>
        {subtitulo ? <p className="truncate text-xs text-adega-muted">{subtitulo}</p> : null}

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
              COR_URGENCIA[bottle.urgency] ?? ''
            }`}
          >
            {URGENCY_LABELS[bottle.urgency]}
          </span>
          <span className="rounded-full bg-adega-surface-2 px-2 py-0.5 text-[10px] text-adega-muted">
            {/* "Sem posição" só diz algo quando existe posição a ter. Para quem
                não cadastrou adega nenhuma, toda garrafa estaria marcada assim —
                ruído em todas as linhas, informando nada. */}
            {quantidade > 1
              ? `${quantidade} garrafas`
              : (bottle.positionLabel ?? (semAdegas ? 'Guardada por aí' : 'Sem posição'))}
          </span>
        </div>

        {quantidade > 1 ? (
          <p className="mt-1.5 text-xs text-adega-muted">📍 {posicoes.join(' · ')}</p>
        ) : null}

        {extra}
      </div>
    </Link>
  );
}
